import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaBell } from '@react-icons/all-files/fa/FaBell';
import { FaCheckCircle } from '@react-icons/all-files/fa/FaCheckCircle';
import { FaEnvelope } from '@react-icons/all-files/fa/FaEnvelope';
import { FaExclamationCircle } from '@react-icons/all-files/fa/FaExclamationCircle';
import { FaUserCircle } from '@react-icons/all-files/fa/FaUserCircle';
import ProfaseModal from './ProfaseModal';
import {
  fetchUsuarioMail,
  refreshUsuarioMailStatus,
  UsuarioMailStatus,
  usuarioMailErrorMessage,
  validarUsuarioMail,
} from '../shared/usuario-mail';
import {
  enviarNotificacionFirebasePrueba,
  fetchUsuarioFirebaseVinculo,
  limpiarTokensFirebase,
  usuarioFirebaseErrorMessage,
} from '../shared/usuario-firebase';
import { notifyCrmLocal } from '../shared/crm-local-notify';
import { renewCurrentBrowserNotification } from '../config/firebase';

export interface AccountModalProps {
  open: boolean;
  onClose: () => void;
  userId?: number | string;
  login: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

function AccountUserAvatar({ userId, size = 64 }: { userId?: number | string; size?: number }) {
  const [errored, setErrored] = useState(false);
  if (!errored && userId) {
    return (
      <img
        src={`/public/usuario/${userId}/image.png`}
        alt="avatar"
        width={size}
        height={size}
        onError={() => setErrored(true)}
        style={{ borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
      />
    );
  }
  return <FaUserCircle className="account-modal__avatar-fallback" style={{ fontSize: size }} />;
}

export default function AccountModal({
  open,
  onClose,
  userId,
  login,
  firstName,
  lastName,
  email,
}: AccountModalProps) {
  const displayName = [firstName, lastName].filter(Boolean).join(' ') || login || 'Usuario';
  const accountEmail = email?.trim() ?? '';

  const [mailStatus, setMailStatus] = useState<UsuarioMailStatus | null>(null);
  const [loadingMail, setLoadingMail] = useState(false);
  const [validating, setValidating] = useState(false);
  const [mailError, setMailError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [pushVinculado, setPushVinculado] = useState<boolean | null>(null);
  const [loadingPush, setLoadingPush] = useState(false);
  const [testingPush, setTestingPush] = useState(false);
  const [pinging, setPinging] = useState(false);
  const [clearingTokens, setClearingTokens] = useState(false);
  const [pushError, setPushError] = useState<string | null>(null);
  const [pushSuccess, setPushSuccess] = useState<string | null>(null);

  const loadPushStatus = useCallback(async () => {
    setLoadingPush(true);
    setPushError(null);
    setPushSuccess(null);
    try {
      const vinculo = await fetchUsuarioFirebaseVinculo();
      setPushVinculado(vinculo.vinculado);
    } catch (err) {
      setPushVinculado(null);
      setPushError(usuarioFirebaseErrorMessage(err));
    } finally {
      setLoadingPush(false);
    }
  }, []);

  const loadMailStatus = useCallback(async () => {
    setLoadingMail(true);
    setMailError(null);
    setSuccessMessage(null);
    try {
      let mail = await fetchUsuarioMail();
      if (mail?.puedeEnviar) {
        setMailStatus(mail);
        return;
      }
      if (mail?.email) {
        try {
          mail = await refreshUsuarioMailStatus();
        } catch {
          // Sin config usable en SES; mantener lo que haya en local.
        }
      }
      setMailStatus(mail);
    } catch (err) {
      setMailStatus(null);
      setMailError(usuarioMailErrorMessage(err));
    } finally {
      setLoadingMail(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      loadMailStatus();
      loadPushStatus();
    } else {
      setMailStatus(null);
      setMailError(null);
      setSuccessMessage(null);
      setLoadingMail(false);
      setValidating(false);
      setPushVinculado(null);
      setPushError(null);
      setPushSuccess(null);
      setLoadingPush(false);
      setTestingPush(false);
      setPinging(false);
      setClearingTokens(false);
    }
  }, [open, loadMailStatus, loadPushStatus]);

  const handleVerify = async () => {
    if (!accountEmail) return;
    setValidating(true);
    setMailError(null);
    setSuccessMessage(null);
    try {
      if (mailStatus?.email) {
        try {
          const refreshed = await refreshUsuarioMailStatus();
          setMailStatus(refreshed);
          if (refreshed.puedeEnviar) {
            setSuccessMessage('Mail verificado correctamente.');
            return;
          }
        } catch {
          // Sin config usable en SES; continuar con validar.
        }
      }

      const updated = await validarUsuarioMail(accountEmail, displayName !== login ? displayName : undefined);
      setMailStatus(updated);
      if (updated.puedeEnviar) {
        setSuccessMessage('Mail verificado correctamente.');
      } else {
        setSuccessMessage('Revisá tu correo y hacé clic en el enlace de verificación.');
      }
    } catch (err) {
      setMailError(usuarioMailErrorMessage(err));
    } finally {
      setValidating(false);
    }
  };

  const handleTestPush = async () => {
    setTestingPush(true);
    setPushError(null);
    setPushSuccess(null);
    try {
      await enviarNotificacionFirebasePrueba();
      setPushSuccess('Notificación de prueba enviada.');
    } catch (err) {
      setPushError(usuarioFirebaseErrorMessage(err));
    } finally {
      setTestingPush(false);
    }
  };

  const handlePingLocal = async () => {
    setPinging(true);
    setPushError(null);
    setPushSuccess(null);
    const ok = await notifyCrmLocal({
      titulo: 'Ping',
      mensaje: 'Servicio local en el puerto 29876.',
      data: { source: 'ping' },
      detalles: 'Prueba desde Cuenta',
    });
    if (ok) {
      setPushSuccess('Ping OK: servicio local en el puerto 29876.');
    } else {
      setPushError('No se pudo contactar el servicio en localhost:29876.');
    }
    setPinging(false);
  };

  const handleLimpiarTokens = async () => {
    setClearingTokens(true);
    setPushError(null);
    setPushSuccess(null);
    try {
      await limpiarTokensFirebase();
      const result = await renewCurrentBrowserNotification();
      if (!result.success || !result.token) {
        setPushVinculado(false);
        setPushError(result.error || 'Se borraron los tokens, pero no se pudo renovar el de este browser.');
        return;
      }
      setPushVinculado(true);
      setPushSuccess('Tokens limpiados. Se guardó el token de este browser.');
    } catch (err) {
      setPushError(usuarioFirebaseErrorMessage(err));
    } finally {
      setClearingTokens(false);
    }
  };

  const mailUi = useMemo(() => {
    if (loadingMail) {
      return { message: 'Cargando estado del mail…', showVerify: false, showResend: false, verified: false };
    }
    if (mailStatus?.puedeEnviar) {
      return {
        message: 'Tu correo está verificado. Podés enviar mails desde las actividades.',
        showVerify: false,
        showResend: false,
        verified: true,
      };
    }
    if (mailStatus?.verificationStatus === 'Pending') {
      return {
        message: 'Te enviamos un correo. Revisá tu bandeja e inbox de spam.',
        showVerify: false,
        showResend: true,
        verified: false,
      };
    }
    if (
      mailStatus?.verificationStatus === 'Failed'
      || mailStatus?.verificationStatus === 'TemporaryFailure'
    ) {
      return {
        message: 'No se pudo verificar. Pedí un nuevo enlace.',
        showVerify: true,
        showResend: false,
        verified: false,
      };
    }
    return {
      message: 'Validá tu correo para enviar mails.',
      showVerify: true,
      showResend: false,
      verified: false,
    };
  }, [loadingMail, mailStatus]);

  const verifyDisabled = !accountEmail || validating || loadingMail;
  const verifyLabel = mailUi.showResend ? 'Reenviar' : 'Verificar mail';

  return (
    <ProfaseModal
      open={open}
      titleId="account-modal-title"
      size="compact"
    >
      <div className="account-modal__header">
        <h2 id="account-modal-title" className="profase-modal-title m-0">
          <FaUserCircle style={{ marginRight: 8, verticalAlign: '-2px' }} />
          Cuenta
        </h2>
        <button
          type="button"
          className="profase-form-btn-secondary btn-sm"
          onClick={onClose}
          disabled={validating || testingPush || clearingTokens}
        >
          Cerrar
        </button>
      </div>

      <div className="account-modal__body">
        <div className="account-modal__profile-row">
          <AccountUserAvatar userId={userId} size={64} />
          <div className="account-modal__profile-info">
            <div className="account-modal__display-name">{displayName}</div>
            <div className="account-modal__meta-row">
              <span className="account-modal__meta-label">Login</span>
              {login || '—'}
            </div>
            <div className="account-modal__meta-row">
              <span className="account-modal__meta-label">Email</span>
              {accountEmail || '—'}
            </div>
          </div>
        </div>

        <div className="account-modal__mail-section">
          <div className="account-modal__section-title">
            <FaEnvelope style={{ marginRight: 6, verticalAlign: '-2px' }} />
            Mail para envíos
          </div>

          {mailUi.verified && (
            <div className="account-modal__alert account-modal__alert--verified">
              <FaCheckCircle style={{ flexShrink: 0 }} />
              <span>Mail verificado</span>
            </div>
          )}

          <p className="account-modal__mail-message">{mailUi.message}</p>

          {!accountEmail && (
            <div className="account-modal__alert account-modal__alert--warning">
              <FaExclamationCircle style={{ flexShrink: 0 }} />
              <span>No hay email en la cuenta.</span>
            </div>
          )}

          {mailError && (
            <div className="account-modal__alert account-modal__alert--error">{mailError}</div>
          )}

          {successMessage && (
            <div className="account-modal__alert account-modal__alert--success">{successMessage}</div>
          )}

          {(mailUi.showVerify || mailUi.showResend) && (
            <button
              type="button"
              className="profase-form-btn-primary btn-sm"
              style={{ marginTop: 8 }}
              onClick={handleVerify}
              disabled={verifyDisabled}
            >
              {validating ? 'Enviando…' : verifyLabel}
            </button>
          )}
        </div>

        <div className="account-modal__push-section">
          <div className="account-modal__section-title">
            <FaBell style={{ marginRight: 6, verticalAlign: '-2px' }} />
            Push notifications
          </div>

          {loadingPush && (
            <p className="account-modal__mail-message">Cargando estado de push…</p>
          )}

          {!loadingPush && pushVinculado && (
            <div className="account-modal__alert account-modal__alert--verified">
              <FaCheckCircle style={{ flexShrink: 0 }} />
              <span>Vinculado</span>
            </div>
          )}

          {!loadingPush && pushVinculado === false && (
            <div className="account-modal__alert account-modal__alert--warning">
              <FaExclamationCircle style={{ flexShrink: 0 }} />
              <span>No vinculado</span>
            </div>
          )}

          {!loadingPush && pushVinculado && (
            <p className="account-modal__mail-message">
              Esta cuenta tiene un token Firebase habilitado.
            </p>
          )}

          {!loadingPush && pushVinculado === false && (
            <p className="account-modal__mail-message">
              No hay un token Firebase habilitado para esta cuenta. Aceptá el permiso de notificaciones al iniciar sesión.
            </p>
          )}

          {pushError && (
            <div className="account-modal__alert account-modal__alert--error">{pushError}</div>
          )}

          {pushSuccess && (
            <div className="account-modal__alert account-modal__alert--success">{pushSuccess}</div>
          )}

          {pushVinculado && (
            <div className="account-modal__push-actions">
              <button
                type="button"
                className="profase-form-btn-primary btn-sm"
                onClick={handleTestPush}
                disabled={testingPush || loadingPush || pinging || clearingTokens}
              >
                {testingPush ? 'Enviando…' : 'Test'}
              </button>
              <button
                type="button"
                className="profase-form-btn-primary btn-sm"
                onClick={handlePingLocal}
                disabled={pinging || loadingPush || testingPush || clearingTokens}
              >
                {pinging ? 'Ping…' : 'Ping'}
              </button>
              <button
                type="button"
                className="profase-form-btn-secondary btn-sm"
                onClick={handleLimpiarTokens}
                disabled={clearingTokens || loadingPush || testingPush || pinging}
              >
                {clearingTokens ? 'Limpiando…' : 'Limpiar Tokens'}
              </button>
            </div>
          )}
        </div>
      </div>
    </ProfaseModal>
  );
}
